import type {
  BudgetPeriod,
  Commitment,
  ForecastCostpointBreakdown,
  ForecastPeriodType,
  ForecastReport,
  ForecastStatus,
} from '@finapp/types';
import type React from 'react';
import { useMemo, useState } from 'react';
import AddCommitmentModal, { type NewCommitmentData } from '../components/AddCommitmentModal';
import EditCommitmentModal from '../components/EditCommitmentModal';
import useCommitments from '../hooks/useCommitments';
import useForecastData from '../hooks/useForecastData';
import { commitmentsApi } from '../services/commitmentsApi';
import CostpointCoverageCallout from './CostpointCoverageCallout';
import OpeningBalanceForm from './OpeningBalanceForm';
import { getCostpointLabel } from '../utils/costpointLabels';
import { formatEuro } from '../utils/currency';

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const STATUS_BADGE_STYLES: Record<ForecastStatus, string> = {
  on_track: 'bg-green-100 text-green-800',
  at_risk: 'bg-yellow-100 text-yellow-800',
  exceeded: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<ForecastStatus, string> = {
  on_track: 'On Track',
  at_risk: 'At Risk',
  exceeded: 'Exceeded',
};

const PERIOD_TYPE_OPTIONS: Array<{ value: ForecastPeriodType; label: string }> = [
  { value: 'ALL', label: 'All' },
  { value: 'H1', label: 'H1' },
  { value: 'H2', label: 'H2' },
];

// "Year-end"/"H1-end"/"H2-end" - used to make copy like "Projected Year-End Net" period-
// relative, so it doesn't read as a whole-year figure while scoped to just a half.
const PERIOD_END_LABELS: Record<ForecastPeriodType, string> = {
  ALL: 'Year-End',
  H1: 'H1-End',
  H2: 'H2-End',
};

const PERIOD_DESCRIPTION_LABELS: Record<ForecastPeriodType, string> = {
  ALL: 'Year-end',
  H1: 'H1-end',
  H2: 'H2-end',
};

/** Worst-of helper, mirroring ForecastService.worstStatus on the backend (exceeded > at_risk > on_track). */
function worstStatus(...statuses: ForecastStatus[]): ForecastStatus {
  if (statuses.includes('exceeded')) return 'exceeded';
  if (statuses.includes('at_risk')) return 'at_risk';
  return 'on_track';
}

function StatusBadge({ status }: { status: ForecastStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_BADGE_STYLES[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/**
 * The API's "no opening balance recorded yet" signal: opening_balance defaults to 0 and
 * none of the reconciliation fields have ever been set. A deliberately-entered €0 balance
 * would still have a current_balance_updated_at (or a note), so this check does not
 * mistake that for "not set".
 */
function isOpeningBalanceUnset(report: ForecastReport): boolean {
  return (
    report.opening_balance === 0 &&
    report.current_balance_override === null &&
    report.current_balance_note === null &&
    report.current_balance_updated_at === null
  );
}

interface CostpointForecastCardProps {
  breakdown: ForecastCostpointBreakdown;
  periodType: ForecastPeriodType;
}

const CostpointForecastCard: React.FC<CostpointForecastCardProps> = ({
  breakdown,
  periodType,
}) => {
  const overallStatus = worstStatus(breakdown.cost_status, breakdown.revenue_status);

  return (
    <div
      className="bg-white rounded-lg shadow-md overflow-hidden"
      data-testid={`forecast-card-${breakdown.costpoint}`}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {getCostpointLabel(breakdown.costpoint)}
          </h3>
          <StatusBadge status={overallStatus} />
        </div>

        <div className="space-y-4">
          {/* Cost */}
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Cost
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Budgeted</span>
                <span className="font-medium text-blue-600">
                  {formatEuro(breakdown.budgeted_cost)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Actual to date</span>
                <span className="font-medium text-orange-600">
                  {formatEuro(breakdown.actual_cost_to_date)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Remaining budget</span>
                <span className="font-medium text-gray-900">
                  {formatEuro(breakdown.remaining_budgeted_cost)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs pt-1">
                <span className="text-gray-400">Status</span>
                <StatusBadge status={breakdown.cost_status} />
              </div>
            </div>
          </div>

          {/* Revenue */}
          <div className="pt-3 border-t border-gray-100">
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Revenue
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Budgeted</span>
                <span className="font-medium text-blue-600">
                  {formatEuro(breakdown.budgeted_revenue)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Actual to date</span>
                <span className="font-medium text-green-600">
                  {formatEuro(breakdown.actual_revenue_to_date)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Remaining budget</span>
                <span className="font-medium text-gray-900">
                  {formatEuro(breakdown.remaining_budgeted_revenue)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs pt-1">
                <span className="text-gray-400">Status</span>
                <StatusBadge status={breakdown.revenue_status} />
              </div>
            </div>
          </div>

          {/* Projected net */}
          <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
            <span className="text-sm font-medium text-gray-900">
              Projected {PERIOD_END_LABELS[periodType]} Net
            </span>
            <span
              className={`text-sm font-bold ${
                breakdown.projected_net >= 0 ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {formatEuro(breakdown.projected_net)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CommitmentsPanelProps {
  commitments: Commitment[];
  isLoading: boolean;
  error: string | null;
  onAddClick: () => void;
  onEditClick: (commitment: Commitment) => void;
  onRemove: (id: number) => void;
  // Resolves a commitment's budget_period_id to its period_type (H1/H2/ANNUAL) for display -
  // a commitment only carries the id, not the period's own fields.
  periodLabelFor: (budgetPeriodId: number) => string;
}

const CommitmentsPanel: React.FC<CommitmentsPanelProps> = ({
  commitments,
  isLoading,
  error,
  onAddClick,
  onEditClick,
  onRemove,
  periodLabelFor,
}) => {
  return (
    <div className="mb-6 bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Commitments</h3>
            <p className="text-sm text-gray-500">
              Agreed expenses/income already known, not yet showing up as a real transaction.
            </p>
          </div>
          <button
            onClick={onAddClick}
            className="bg-indigo-600 text-white px-3 py-2 text-sm font-medium rounded-md hover:bg-indigo-700"
            data-testid="add-commitment-button"
          >
            + Add commitment
          </button>
        </div>

        {error && <p className="text-sm text-red-700 mb-3">{error}</p>}

        {!isLoading && commitments.length === 0 && !error && (
          <p className="text-sm text-gray-500">No open commitments for this period.</p>
        )}

        {commitments.length > 0 && (
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-gray-200 text-sm"
              data-testid="commitments-table"
            >
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <th className="pb-2 pr-4">Period</th>
                  <th className="pb-2 pr-4">Costpoint</th>
                  <th className="pb-2 pr-4">Type</th>
                  <th className="pb-2 pr-4">Description</th>
                  <th className="pb-2 pr-4">Amount</th>
                  <th className="pb-2 pr-4">Recorded by</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {commitments.map((commitment) => (
                  <tr key={commitment.id} data-testid={`commitment-row-${commitment.id}`}>
                    <td className="py-2 pr-4 text-gray-500">
                      {periodLabelFor(commitment.budget_period_id)}
                    </td>
                    <td className="py-2 pr-4">{getCostpointLabel(commitment.costpoint)}</td>
                    <td className="py-2 pr-4 capitalize">{commitment.type}</td>
                    <td className="py-2 pr-4">{commitment.description}</td>
                    <td className="py-2 pr-4 font-medium">{formatEuro(commitment.amount)}</td>
                    <td className="py-2 pr-4 text-gray-500">{commitment.created_by}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => onEditClick(commitment)}
                        className="text-indigo-700 hover:text-indigo-900 text-xs font-medium mr-3"
                        data-testid={`commitment-edit-${commitment.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onRemove(commitment.id)}
                        className="text-red-700 hover:text-red-900 text-xs font-medium"
                        data-testid={`commitment-remove-${commitment.id}`}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Forecast: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [asOfDate, setAsOfDate] = useState(todayIso());
  const [periodType, setPeriodType] = useState<ForecastPeriodType>('ALL');

  const { forecastData, isLoading, error, reload } = useForecastData(
    selectedYear,
    asOfDate,
    periodType
  );

  const {
    commitments,
    availablePeriods,
    defaultBudgetPeriodId,
    isLoading: commitmentsLoading,
    error: commitmentsError,
    reload: reloadCommitments,
  } = useCommitments(selectedYear, periodType);
  const [isAddCommitmentOpen, setIsAddCommitmentOpen] = useState(false);
  const [editingCommitment, setEditingCommitment] = useState<Commitment | null>(null);

  const periodsById = useMemo(
    () => new Map(availablePeriods.map((p) => [p.id, p] as [number, BudgetPeriod])),
    [availablePeriods]
  );
  const periodLabelFor = (budgetPeriodId: number): string =>
    periodsById.get(budgetPeriodId)?.period_type ?? '—';

  // Reload both the commitment list and the forecast itself, so the newly added/edited/
  // removed amount is reflected in budgeted_cost/revenue right away rather than on next load.
  const handleAddCommitment = async (data: NewCommitmentData) => {
    await commitmentsApi.create(data);
    await Promise.all([reloadCommitments(), reload()]);
  };

  const handleEditCommitment = async (id: number, data: { description: string; amount: number }) => {
    await commitmentsApi.update(id, data);
    await Promise.all([reloadCommitments(), reload()]);
  };

  const handleRemoveCommitment = async (id: number) => {
    await commitmentsApi.remove(id);
    await Promise.all([reloadCommitments(), reload()]);
  };

  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Keep year + as-of-date in sync, since the API 400s if asOfDate falls outside the
  // requested year (see forecast.controller.ts's validateAsOfDateWithinYear).
  const handleYearChange = (newYear: number) => {
    setSelectedYear(newYear);
    setAsOfDate(newYear === currentYear ? todayIso() : `${newYear}-12-31`);
  };

  const handleAsOfDateChange = (newDate: string) => {
    setAsOfDate(newDate);
    const newYear = Number(newDate.slice(0, 4));
    if (Number.isInteger(newYear) && newYear !== selectedYear) {
      setSelectedYear(newYear);
    }
  };

  const openingBalanceUnset = forecastData !== null && isOpeningBalanceUnset(forecastData);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Forecast</h1>
        <p className="mt-2 text-sm text-gray-700">
          {PERIOD_DESCRIPTION_LABELS[periodType]} projection of budgeted vs. actual cost and
          revenue, per costpoint and org-wide.
        </p>
      </div>

      {/* Year + As Of + Period selector */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex flex-col">
            <label
              htmlFor="forecast-year-select"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              Year
            </label>
            <select
              id="forecast-year-select"
              value={selectedYear}
              onChange={(e) => handleYearChange(parseInt(e.target.value, 10))}
              disabled={isLoading}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label
              htmlFor="forecast-as-of-date"
              className="text-sm font-medium text-gray-700 mb-1"
            >
              As of date
            </label>
            <input
              id="forecast-as-of-date"
              type="date"
              value={asOfDate}
              onChange={(e) => handleAsOfDateChange(e.target.value)}
              disabled={isLoading}
              className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <fieldset className="flex flex-col border-0 p-0 m-0">
            <legend className="text-sm font-medium text-gray-700 mb-1 p-0">Period</legend>
            <div
              className="inline-flex rounded-md shadow-sm"
              data-testid="forecast-period-type-selector"
            >
              {PERIOD_TYPE_OPTIONS.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={isLoading}
                  aria-pressed={periodType === option.value}
                  onClick={() => setPeriodType(option.value)}
                  data-testid={`forecast-period-type-${option.value}`}
                  className={`px-3 py-2 text-sm font-medium border disabled:cursor-not-allowed disabled:opacity-60 ${
                    index === 0 ? 'rounded-l-md' : '-ml-px'
                  } ${index === PERIOD_TYPE_OPTIONS.length - 1 ? 'rounded-r-md' : ''} ${
                    periodType === option.value
                      ? 'bg-indigo-600 border-indigo-600 text-white z-10'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
              Loading forecast...
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading forecast</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={reload}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Initial Loading State */}
      {isLoading && !forecastData && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          <span className="ml-3 text-gray-600">Loading forecast data...</span>
        </div>
      )}

      {/* Content */}
      {forecastData && (
        <>
          {/* Org-wide summary panel */}
          <div className="mb-6 bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Organization-wide Summary
                </h3>
                <StatusBadge status={forecastData.org_status} />
              </div>

              {openingBalanceUnset && (
                <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                  <p className="text-sm text-blue-700">
                    No opening balance has been recorded for {selectedYear} yet. Figures below
                    assume a €0 opening balance until one is set.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Opening Balance</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatEuro(forecastData.opening_balance)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Current Computed Balance</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatEuro(forecastData.org_current_computed_balance)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">
                    Projected {PERIOD_END_LABELS[forecastData.period_type]} Net
                  </div>
                  <div
                    className={`text-lg font-semibold ${
                      forecastData.org_projected_net >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {formatEuro(forecastData.org_projected_net)}
                  </div>
                </div>
                {forecastData.current_balance_override !== null && (
                  <div>
                    <div className="text-sm text-gray-500">Manual Override Balance</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {formatEuro(forecastData.current_balance_override)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Variance (computed − override):{' '}
                      {formatEuro(
                        forecastData.org_current_computed_balance -
                          forecastData.current_balance_override
                      )}
                    </div>
                  </div>
                )}
              </div>

              <CostpointCoverageCallout
                costpointBreakdown={forecastData.costpoint_breakdown}
                orgProjectedNet={forecastData.org_projected_net}
              />

              <OpeningBalanceForm
                year={selectedYear}
                report={forecastData}
                isUnset={openingBalanceUnset}
                onSaved={reload}
              />

              {forecastData.current_balance_note && (
                <p className="mt-4 text-xs text-gray-500 italic">
                  Note: {forecastData.current_balance_note}
                </p>
              )}
              {forecastData.current_balance_updated_at && (
                <p className="mt-1 text-xs text-gray-400">
                  Balance override last updated{' '}
                  {new Date(forecastData.current_balance_updated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {/* Commitments */}
          <CommitmentsPanel
            commitments={commitments}
            isLoading={commitmentsLoading}
            error={commitmentsError}
            onAddClick={() => setIsAddCommitmentOpen(true)}
            onEditClick={setEditingCommitment}
            onRemove={(id) => {
              if (confirm('Remove this commitment? This cannot be undone.')) {
                handleRemoveCommitment(id);
              }
            }}
            periodLabelFor={periodLabelFor}
          />

          {/* Per-costpoint cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {forecastData.costpoint_breakdown.map((breakdown) => (
              <CostpointForecastCard
                key={breakdown.costpoint}
                breakdown={breakdown}
                periodType={forecastData.period_type}
              />
            ))}
          </div>
        </>
      )}

      <AddCommitmentModal
        isOpen={isAddCommitmentOpen}
        onClose={() => setIsAddCommitmentOpen(false)}
        onSave={handleAddCommitment}
        availablePeriods={availablePeriods}
        defaultBudgetPeriodId={defaultBudgetPeriodId}
        year={selectedYear}
      />

      <EditCommitmentModal
        commitment={editingCommitment}
        periodLabel={editingCommitment ? periodLabelFor(editingCommitment.budget_period_id) : ''}
        onClose={() => setEditingCommitment(null)}
        onSave={handleEditCommitment}
      />
    </div>
  );
};

export default Forecast;
